import { useParams } from 'react-router-dom';
import { useState } from 'react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { AxiosError } from 'axios';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { useMutatePublishProject } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { ECompanyThemeName } from '@/enums';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import { PublishProjectModalProps } from '@/types';

function PublishProjectModal({
  isProjectInDraftStatus,
  projectTitle,
  publishTooltipText,
  projectId,
  isPublishRequestApproved,
  canPublish,
  isWorkspaceViewer = false,
}: PublishProjectModalProps) {
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { lang } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const { themeName } = useTheme();

  const { mutateAsync: mutateAsyncPublishProject, isPending } = useMutatePublishProject(
    organizationId,
    workspaceId,
  );

  const handlePublishProject = async (formData?: string) => {
    try {
      await mutateAsyncPublishProject(formData);
      toast.success(
        `${lang.get('msg.project')} "${projectTitle}" ${lang.get('msg.isSuccessfullyPublished')}`,
      );
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.status === StatusCodes.FORBIDDEN) {
        toast.error(
          themeName === ECompanyThemeName.EMPATHY
            ? lang.get('msg.projectSubscriptionPublishError_empathy')
            : lang.get('msg.projectSubscriptionPublishError'),
        );
      } else {
        toast.error(lang.get('msg.projectIsNotPublishedPleaseTryAgain'));
      }
    }
  };

  const isPublishState = canPublish && !isPublishRequestApproved;
  const isEmpathyTheme = themeName === ECompanyThemeName.EMPATHY;

  // Determine if button should be disabled and what tooltip to show
  const isButtonDisabled = isProjectInDraftStatus || isWorkspaceViewer;
  const tooltipText = isWorkspaceViewer
    ? lang.get('msg.workspaceViewerCannotPublish')
    : publishTooltipText;

  return (
    <AlertDialog open={openModal}>
      <TooltipWrapper
        text={tooltipText}
        className={cn(
          isButtonDisabled ? 'flex' : 'hidden',
          'relative max-w-[360px] text-center mr-10',
        )}
      >
        {/* without div, I can't hover and get tooltip text, because button is disabled */}
        <div>
          <Button
            variant="default"
            disabled={isButtonDisabled}
            type="button"
            className="h-8 px-5"
            onClick={() => setOpenModal(true)}
          >
            {isPublishState && lang.get('msg.publish')}
            {isPublishRequestApproved && lang.get('msg.requestApproved')}
          </Button>
        </div>
      </TooltipWrapper>
      {openModal && (
        <AlertDialogContent className="max-w-l overflow-hidden flex flex-col gap-14">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-wrap items-center gap-2">
              {lang.get('msg.publish')} {isEmpathyTheme ? 'project' : 'Tellet'}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {isEmpathyTheme
                ? lang.get('msg.areYouSureYouWantToPublishThisTellet_empathy')
                : lang.get('msg.areYouSureYouWantToPublishThisTellet')}
              <span className="font-semibold">{` - ${projectTitle}?`}</span>
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setOpenModal(!openModal)}>
              {lang.get('msg.cancel')}
            </AlertDialogCancel>
            <Button
              variant="default"
              onClick={() => {
                handlePublishProject(projectId);
                setOpenModal(false);
              }}
              disabled={isPending}
              type="button"
            >
              {lang.get('msg.publish')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}

export default PublishProjectModal;
